import type { OrganizationManager } from "@entities";
import { Autocomplete, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { PARTNER_ORG_HANDLED_BY_OPTIONS } from "@shared/helpers/partner-organization-handled-by";
import { CooperationLevelPicker } from "./CooperationLevelPicker";
import { ChoiceTile, FormSection } from "./partner-organization-form-ui";
import type { PartnerOrganizationFormValues } from "./partner-organization-form";

type Props = {
  values: PartnerOrganizationFormValues;
  onChange: (next: PartnerOrganizationFormValues) => void;
  responsibleManager: OrganizationManager | null;
  onResponsibleManagerChange: (manager: OrganizationManager | null) => void;
  responsibleManagerSearch: string;
  onResponsibleManagerSearchChange: (value: string) => void;
  responsibleManagerOptions: OrganizationManager[];
};

export function PartnerOrganizationHandlingFields({
  values,
  onChange,
  responsibleManager,
  onResponsibleManagerChange,
  responsibleManagerSearch,
  onResponsibleManagerSearchChange,
  responsibleManagerOptions,
}: Props) {
  return (
    <Stack gap={2.5}>
      <FormSection
        title="מי מטפל בארגון"
        description="צוות הקשר עם הארגון. חיפוש מתנדבים נשאר משותף לכל המנהלים."
      >
        <Stack gap={2.5}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            {PARTNER_ORG_HANDLED_BY_OPTIONS.map((option) => {
              const selected = values.handledBy === option.value;
              return (
                <ChoiceTile
                  key={option.value}
                  selected={selected}
                  label={option.label}
                  description={
                    option.value === "CALL_CENTER"
                      ? "המוקד מוביל את הקשר"
                      : "רכזות השטח מובילות את הקשר"
                  }
                  onClick={() =>
                    onChange({
                      ...values,
                      handledBy: selected ? "" : option.value,
                    })
                  }
                />
              );
            })}
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} gap={2}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={
                responsibleManager &&
                !responsibleManagerOptions.some(
                  (manager) => manager.id === responsibleManager.id,
                )
                  ? [responsibleManager, ...responsibleManagerOptions]
                  : responsibleManagerOptions
              }
              value={responsibleManager}
              onChange={(_, manager) => {
                onResponsibleManagerChange(manager);
                onChange({
                  ...values,
                  handlingManagerId: manager?.id ?? "",
                });
              }}
              inputValue={responsibleManagerSearch}
              onInputChange={(_, value) =>
                onResponsibleManagerSearchChange(value)
              }
              getOptionLabel={(manager) =>
                `${manager.name}${manager.organization?.name ? ` · ${manager.organization.name}` : ""}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="הקלד לפחות 2 תווים לחיפוש"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="מנהל אחראי"
                  helperText="מנהל אחד שאחראי על הקשר. לא נועל את חיפוש המתנדבים."
                />
              )}
            />
            <DatePicker
              label="לחזור בתאריך"
              value={values.nextFollowUpAt ? dayjs(values.nextFollowUpAt) : null}
              onChange={(value) =>
                onChange({
                  ...values,
                  nextFollowUpAt: value?.isValid()
                    ? value.format("YYYY-MM-DD")
                    : "",
                })
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText:
                    "אם הגיע התאריך, הארגון מופיע בשבב דורש מגע. בלי תאריך - 14 ימים בלי תיעוד.",
                },
                field: { clearable: true },
              }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
      </FormSection>
      <FormSection
        title="דירוג רצינות"
        description="1 הכי גבוה · 9 הכי נמוך. A טוב · C חלש"
      >
        <TextField
          label="יעד מתנדבים יומי"
          value={values.dailyVolunteerTarget}
          onChange={(event) =>
            onChange({ ...values, dailyVolunteerTarget: event.target.value })
          }
          placeholder="ריק = בלי יעד"
          helperText="למעקב ולסינון בטבלת הארגונים. ההרשמה נעצרת רק לפי היעד היומי של המשימה."
          fullWidth
          inputProps={{ inputMode: "numeric" }}
        />
        <CooperationLevelPicker
          value={values.cooperationLevel}
          onChange={(cooperationLevel) =>
            onChange({ ...values, cooperationLevel })
          }
        />
      </FormSection>
    </Stack>
  );
}
